import { useState, useRef, useMemo, useEffect } from 'react';
import {
  Upload,
  Download,
  Flag,
  CheckCircle,
  CheckCircle2,
  XCircle,
  AlertCircle,
  AlertTriangle,
  FileDown,
  Pencil,
  ListFilter,
  Files,
  ChevronLeft,
  ChevronRight,
  Info,
} from 'lucide-react';
import ConfirmationModal from '@shared/components/common/ConfirmationModal';
import { cutoverApi } from '../../services/cutoverApi';
import type {
  JobSiteImportPreviewDto,
  JobSiteImportRowDto,
  JobSiteImportRowStatus,
} from '../../types/cutover';
import './ClinicImportTab.css';
import './JobSiteImportTab.css';

type Step = 'upload' | 'preview' | 'result';
type StatusFilter = 'all' | JobSiteImportRowStatus;

const STORAGE_KEY = 'jobsite-import-preview-v1';
const PAGE_SIZE = 50;

// Rows created per Execute call. Small enough that one request stays well inside the
// gateway timeout, large enough that ~2,500 rows finish in a couple of dozen calls.
const BATCH_SIZE = 100;

interface CachedState {
  step: Step;
  preview: JobSiteImportPreviewDto | null;
  fileName: string | null;
}

interface RunTotals {
  created: number;
  skipped: number;
  failed: number;
  errors: string[];
}

const EMPTY_TOTALS: RunTotals = { created: 0, skipped: 0, failed: 0, errors: [] };

function loadCachedState(): CachedState | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as CachedState) : null;
  } catch {
    return null;
  }
}

function saveCachedState(state: CachedState | null) {
  try {
    if (state) sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    else sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // sessionStorage unavailable — preview just won't survive a refresh
  }
}

type ApiErrorBody = { message?: string; errors?: Record<string, string[]> };

/**
 * The API's own explanation: `{ message }` from the cutover endpoints, or the first
 * model-validation error. Transport errors ("Request failed with status code 500",
 * timeouts) say nothing useful to an operator, so they get the caller's fallback.
 */
function errorMessage(err: unknown, fallback: string): string {
  const body = (err as { response?: { data?: ApiErrorBody } })?.response?.data;
  if (body?.message) return body.message;
  const firstValidationError = body?.errors && Object.values(body.errors).flat()[0];
  return firstValidationError || fallback;
}

/** Same as errorMessage for a blob request, whose error body arrives as a Blob. */
async function blobErrorMessage(err: unknown, fallback: string): Promise<string> {
  const data = (err as { response?: { data?: unknown } })?.response?.data;
  if (data instanceof Blob) {
    try {
      const parsed = JSON.parse(await data.text()) as ApiErrorBody;
      if (parsed.message) return parsed.message;
    } catch {
      // not JSON — use the fallback
    }
  }
  return errorMessage(err, fallback);
}

function httpStatus(err: unknown): number | undefined {
  return (err as { response?: { status?: number } })?.response?.status;
}

/** Escape a CSV field per RFC 4180. */
function csv(value: string | number | null | undefined): string {
  const s = String(value ?? '');
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

/**
 * The results as this browser last saw them, for when the server's copy has expired
 * (TTL, restart or redeploy) — the created Location Ids must never become unreachable.
 */
function resultsCsv(rows: JobSiteImportRowDto[]): Blob {
  const header = 'Row,Customer XM ID,Customer Name,SourceLink Customer,JobSite Name,City,State,Zip,' +
    'Model Location Id,Street,Code,Status,Detail,New Location Id,Existing Location Id';
  const lines = rows.map(r => [
    r.rowNumber, r.customerXmId, r.customerName, r.resolvedCustomerName, r.name, r.city, r.state, r.zip,
    r.modelLocationId, r.street, r.code, r.status, r.statusDetail, r.createdLocationId, r.existingLocationId,
  ].map(csv).join(','));
  return new Blob([[header, ...lines].join('\n')], { type: 'text/csv;charset=utf-8' });
}

// A positive whole number, or blank for "no model location".
const MODEL_ID_PATTERN = /^[1-9]\d*$/;

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  // Attach before clicking and defer revocation — Safari can drop the download
  // for detached anchors or immediately-revoked object URLs.
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

const STATUS_LABEL: Record<JobSiteImportRowStatus, string> = {
  Ready: 'Ready',
  DuplicateInSystem: 'Duplicate (in system)',
  DuplicateInFile: 'Duplicate (in file)',
  Invalid: 'Invalid',
  Created: 'Created',
  Failed: 'Failed',
};

const EDIT_FIELDS: [string, keyof JobSiteImportRowDto][] = [
  ['Customer XM ID*', 'customerXmId'],
  ['Customer Name*', 'customerName'],
  ['JobSite Name*', 'name'],
  ['City*', 'city'],
  ['State*', 'state'],
  ['Zip*', 'zip'],
  ['Model Location Id', 'modelLocationId'],
  ['Street', 'street'],
  ['Code', 'code'],
];

/**
 * JobSite bulk import (SLV2-4126) for the Clinic Matrix cutover. Same flow as the
 * Clinic Import tab — template, upload and preview, correct rows, import — but the
 * import runs in batches with progress, and the results download carries each new
 * Location Id for the Clinic Matrix "Location Id" column.
 */
export default function JobSiteImportTab() {
  const cached = useMemo(() => loadCachedState(), []);
  const [step, setStep] = useState<Step>(cached?.step ?? 'upload');
  const [file, setFile] = useState<File | null>(null);
  const [fileName, setFileName] = useState<string | null>(cached?.fileName ?? null);
  const [preview, setPreview] = useState<JobSiteImportPreviewDto | null>(cached?.preview ?? null);
  const [totals, setTotals] = useState<RunTotals>(EMPTY_TOTALS);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editRow, setEditRow] = useState<JobSiteImportRowDto | null>(null);
  // The Model Location Id as typed, so a typo is shown and refused rather than silently
  // becoming "no model location".
  const [editModelText, setEditModelText] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const stopRequested = useRef(false);

  // Leaving the tab unmounts it mid-run: stop after the batch in flight instead of
  // running on with no Stop button. Created rows are kept; Resume continues.
  useEffect(() => () => { stopRequested.current = true; }, []);

  const editModelTrimmed = editModelText.trim();
  const editModelInvalid = editModelTrimmed !== '' && !MODEL_ID_PATTERN.test(editModelTrimmed);

  const openEditor = (row: JobSiteImportRowDto) => {
    setEditRow({ ...row });
    setEditModelText(row.modelLocationId == null ? '' : String(row.modelLocationId));
  };

  const updatePreview = (next: JobSiteImportPreviewDto, nextStep: Step = step) => {
    setPreview(next);
    saveCachedState({ step: nextStep, preview: next, fileName });
  };

  const handleSaveEdit = async () => {
    if (!preview || !editRow || editModelInvalid) return;
    setSavingEdit(true);
    setError(null);
    try {
      const row = { ...editRow, modelLocationId: editModelTrimmed === '' ? null : Number(editModelTrimmed) };
      const updated = await cutoverApi.updateJobSiteImportRow(preview.token, row);
      updatePreview(updated);
      setEditRow(null);
    } catch (err: unknown) {
      setError(errorMessage(err, 'Failed to save the correction'));
    } finally {
      setSavingEdit(false);
    }
  };

  const setEditField = (key: keyof JobSiteImportRowDto, value: string) => {
    if (key === 'modelLocationId') {
      setEditModelText(value);
      return;
    }
    setEditRow(prev => (prev ? { ...prev, [key]: value } : prev));
  };

  // Escape dismisses the correction editor (matches the other dialogs in the app).
  // The backdrop does not (SLV2-4049): a stray click must not discard an edit.
  useEffect(() => {
    if (!editRow) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !savingEdit) setEditRow(null);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [editRow, savingEdit]);

  const handleDownloadTemplate = async () => {
    setError(null);
    try {
      const blob = await cutoverApi.downloadJobSiteImportTemplate();
      downloadBlob(blob, 'jobsite-import-template.xlsx');
    } catch (err: unknown) {
      setError(errorMessage(err, 'Failed to download the template'));
    }
  };

  const handleDownloadResults = async () => {
    if (!preview) return;
    setError(null);
    try {
      const blob = await cutoverApi.downloadJobSiteImportResults(preview.token);
      downloadBlob(blob, 'jobsite-import-results.xlsx');
    } catch (err: unknown) {
      if (httpStatus(err) === 404) {
        // The server's copy of this import has expired; the rows this browser holds still
        // carry every created Location Id.
        downloadBlob(resultsCsv(preview.rows), 'jobsite-import-results.csv');
        setError("The server's copy of this import has expired, so the results were saved from this browser's copy (.csv).");
        return;
      }
      setError(await blobErrorMessage(err, 'Failed to download the results'));
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0] ?? null;
    setFile(f);
    setFileName(f?.name ?? null);
    setError(null);
  };

  const handlePreview = async () => {
    if (!file) return;
    setLoading(true);
    setError(null);
    try {
      const data = await cutoverApi.uploadJobSiteImport(file);
      setPreview(data);
      setTotals(EMPTY_TOTALS);
      setStatusFilter('all');
      setPage(1);
      setStep('preview');
      saveCachedState({ step: 'preview', preview: data, fileName: file.name });
    } catch (err: unknown) {
      setError(errorMessage(err, 'Failed to parse file'));
    } finally {
      setLoading(false);
    }
  };

  /** Runs batches until no Ready rows remain, the operator stops, or a call fails. */
  const handleExecute = async () => {
    if (!preview) return;
    setConfirmOpen(false);
    setRunning(true);
    setError(null);
    stopRequested.current = false;

    let current = preview;
    // Carry on from an earlier, stopped run so Resume shows one continuous total.
    const runTotals: RunTotals = { ...totals, errors: [...totals.errors] };
    try {
      while (current.readyCount > 0 && !stopRequested.current) {
        const batch = await cutoverApi.executeJobSiteImportBatch(current.token, BATCH_SIZE);
        current = batch.preview;
        runTotals.created += batch.created;
        runTotals.skipped += batch.skipped;
        runTotals.failed += batch.failed;
        runTotals.errors.push(...batch.errors);
        setTotals({ ...runTotals, errors: [...runTotals.errors] });
        updatePreview(current, 'preview');
        // Defensive: a batch that touched nothing would otherwise loop forever.
        if (batch.created + batch.skipped + batch.failed === 0) break;
      }
      if (current.readyCount === 0) {
        setStep('result');
        saveCachedState({ step: 'result', preview: current, fileName });
      }
    } catch (err: unknown) {
      // Rows already created stay Created; Resume picks up the remaining Ready rows.
      const reason = errorMessage(err, 'A batch failed.');
      setError(`${reason} Import stopped — rows already created are kept; resume to continue.`);
    } finally {
      setRunning(false);
    }
  };

  const handleReset = () => {
    setStep('upload');
    setFile(null);
    setFileName(null);
    setPreview(null);
    setTotals(EMPTY_TOTALS);
    setError(null);
    setStatusFilter('all');
    saveCachedState(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const filteredRows = useMemo(() => {
    if (!preview) return [];
    return statusFilter === 'all' ? preview.rows : preview.rows.filter(r => r.status === statusFilter);
  }, [preview, statusFilter]);

  const totalPages = Math.max(1, Math.ceil(filteredRows.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pagedRows = useMemo(
    () => filteredRows.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE),
    [filteredRows, currentPage]
  );

  const countOf = (status: JobSiteImportRowStatus) => preview?.rows.filter(r => r.status === status).length ?? 0;
  const problemCount = preview ? preview.totalRows - preview.readyCount - preview.createdCount : 0;
  const runTarget = preview ? preview.readyCount + totals.created + totals.skipped + totals.failed : 0;
  const runDone = totals.created + totals.skipped + totals.failed;
  const progressPct = runTarget > 0 ? Math.round((runDone / runTarget) * 100) : 0;
  const hasStarted = (preview?.createdCount ?? 0) > 0 || (preview?.failedCount ?? 0) > 0;

  const statusBadge = (row: JobSiteImportRowDto) => (
    <span className={`ci-badge ci-badge-${row.status.toLowerCase()}`} title={row.statusDetail ?? undefined}>
      {STATUS_LABEL[row.status]}
    </span>
  );

  const filterChip = (
    value: StatusFilter,
    label: string,
    count: number,
    icon: React.ReactNode,
    tone: 'all' | 'ready' | 'dup' | 'invalid'
  ) => (
    <button
      type="button"
      className={`ci-chip ci-chip-${tone} ${statusFilter === value ? 'active' : ''}`}
      onClick={() => { setStatusFilter(value); setPage(1); }}
      aria-pressed={statusFilter === value}
    >
      <span className="ci-chip-icon">{icon}</span>
      <span className="ci-chip-meta">
        <span className="ci-chip-count">{count}</span>
        <span className="ci-chip-label">{label}</span>
      </span>
    </button>
  );

  const readyCount = preview?.readyCount ?? 0;
  const withoutModel = preview?.withoutModelLocationCount ?? 0;

  return (
    <div className="ci-tab">
      <div className="ci-header">
        <div className="ci-header-title">
          <span className="ci-header-icon"><Flag size={20} /></span>
          <h2>JobSite Import</h2>
        </div>
        <p>
          Bulk-create JobSites (temporary locations) from a spreadsheet. Customers are matched by
          <strong> XM ID</strong>. A JobSite without a model location bills to the customer and
          <strong> can't be used on orders</strong>.
        </p>
      </div>

      {error && (
        <div className="ci-error" role="alert">
          <XCircle size={16} />
          <span>{error}</span>
        </div>
      )}

      {step === 'upload' && (
        <div className="ci-upload-section">
          <div className="ci-step-card">
            <span className="ci-step-num">1</span>
            <div className="ci-step-body">
              <h3>Download the template</h3>
              <p>Paste your JobSite list into it — the column headers must match exactly.</p>
              <button type="button" className="ci-btn ci-btn-secondary" onClick={handleDownloadTemplate}>
                <Download size={16} />
                Download template (.xlsx)
              </button>
            </div>
          </div>

          <div className="ci-step-card">
            <span className="ci-step-num">2</span>
            <div className="ci-step-body">
              <h3>Upload the filled-in sheet</h3>
              <p>Every row is validated and previewed — nothing is created until you confirm.</p>
              <div className="ci-upload-row">
                <button
                  type="button"
                  className="ci-file-label"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <Upload size={16} />
                  <span>Choose file…</span>
                </button>
                <span className={`ci-filename ${fileName ? '' : 'ci-filename-empty'}`}>
                  {fileName ?? 'No file selected'}
                </span>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx"
                onChange={handleFileChange}
                className="ci-file-input"
              />
              <button
                type="button"
                className="ci-btn ci-btn-primary"
                onClick={handlePreview}
                disabled={!file || loading}
              >
                {loading ? 'Validating…' : 'Preview'}
              </button>
            </div>
          </div>
        </div>
      )}

      {step === 'preview' && preview && (
        <div className="ci-preview-section">
          <div className="ci-chips">
            {filterChip('all', 'All rows', preview.totalRows, <ListFilter size={16} />, 'all')}
            {filterChip('Ready', 'Ready to import', preview.readyCount, <CheckCircle2 size={16} />, 'ready')}
            {preview.createdCount > 0 &&
              filterChip('Created', 'Created', preview.createdCount, <CheckCircle size={16} />, 'ready')}
            {filterChip('DuplicateInSystem', 'Already in system',
              countOf('DuplicateInSystem'), <AlertTriangle size={16} />, 'dup')}
            {filterChip('DuplicateInFile', 'Duplicated in file',
              countOf('DuplicateInFile'), <Files size={16} />, 'dup')}
            {filterChip('Invalid', 'Needs fixing', preview.invalidCount, <XCircle size={16} />, 'invalid')}
            {preview.failedCount > 0 &&
              filterChip('Failed', 'Failed', preview.failedCount, <AlertCircle size={16} />, 'invalid')}
            {problemCount > 0 && !hasStarted && (
              // Every row with its status and reason, so problems can be fixed in the source sheet.
              <button type="button" className="ci-btn ci-btn-secondary ci-export-btn" onClick={handleDownloadResults}>
                <FileDown size={14} />
                Export rows with reasons
              </button>
            )}
          </div>

          {withoutModel > 0 && (
            <div className="jsi-note" role="status">
              <Info size={16} />
              <span>
                <strong>{withoutModel}</strong> of the Ready JobSites {withoutModel === 1 ? 'has' : 'have'} no model
                location. {withoutModel === 1 ? 'It' : 'They'} will bill to the customer and can't be used on
                orders.
              </span>
            </div>
          )}

          {running && (
            <div className="jsi-progress" role="status" aria-live="polite">
              <div className="jsi-progress-text">
                Creating JobSites… {runDone.toLocaleString()} of {runTarget.toLocaleString()} processed
                ({totals.created.toLocaleString()} created)
              </div>
              <div
                className="jsi-progress-bar"
                role="progressbar"
                aria-label="JobSite import progress"
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={progressPct}
              >
                <span style={{ width: `${progressPct}%` }} />
              </div>
            </div>
          )}

          <div className="ci-table-wrapper">
            <table className="ci-table">
              <thead>
                <tr>
                  <th>Row</th>
                  <th>Customer</th>
                  <th>JobSite Name</th>
                  <th>City</th>
                  <th>State</th>
                  <th>Zip</th>
                  <th>Model Location</th>
                  <th>Status</th>
                  <th>Detail</th>
                  <th className="ci-actions-col">Actions</th>
                </tr>
              </thead>
              <tbody>
                {pagedRows.map((row) => (
                  <tr
                    key={row.rowNumber}
                    className={row.status !== 'Ready' && row.status !== 'Created' ? 'ci-row-muted' : ''}
                  >
                    <td>{row.rowNumber}</td>
                    <td>
                      <span className="jsi-customer">
                        <span>{row.resolvedCustomerName ?? row.customerName}</span>
                        <span className="jsi-customer-xm">XM {row.customerXmId || '—'}</span>
                      </span>
                    </td>
                    <td>{row.name}</td>
                    <td>{row.city}</td>
                    <td>{row.state}</td>
                    <td>{row.zip}</td>
                    <td>{row.modelLocationId ?? <span className="jsi-muted">None</span>}</td>
                    <td>{statusBadge(row)}</td>
                    <td className="ci-detail-cell">
                      {row.createdLocationId
                        ? `Location Id ${row.createdLocationId}`
                        : row.statusDetail ?? (row.existingLocationId ? `Location Id ${row.existingLocationId}` : '')}
                    </td>
                    <td className="ci-edit-cell">
                      {row.status !== 'Created' && (
                        <button
                          type="button"
                          className="ci-edit-btn"
                          aria-label={`Edit row ${row.rowNumber}`}
                          title="Correct this row and re-validate"
                          onClick={() => openEditor(row)}
                          disabled={running}
                        >
                          <Pencil size={12} />
                          <span>Edit</span>
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {filteredRows.length > 0 && (
            <div className="ci-pagination">
              <span className="ci-pagination-info">
                Showing {(currentPage - 1) * PAGE_SIZE + 1}–{Math.min(currentPage * PAGE_SIZE, filteredRows.length)} of {filteredRows.length}
              </span>
              {totalPages > 1 && (
                <span className="ci-pagination-controls">
                  <button
                    type="button"
                    className="ci-btn ci-btn-secondary ci-btn-page"
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={currentPage <= 1}
                  >
                    <ChevronLeft size={14} />
                    Prev
                  </button>
                  <span className="ci-pagination-page">Page {currentPage} of {totalPages}</span>
                  <button
                    type="button"
                    className="ci-btn ci-btn-secondary ci-btn-page"
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={currentPage >= totalPages}
                  >
                    Next
                    <ChevronRight size={14} />
                  </button>
                </span>
              )}
            </div>
          )}

          <div className="ci-actions">
            {running ? (
              <button
                type="button"
                className="ci-btn ci-btn-secondary"
                onClick={() => { stopRequested.current = true; }}
              >
                Stop after this batch
              </button>
            ) : (
              <>
                <button type="button" className="ci-btn ci-btn-secondary" onClick={handleReset}>
                  {hasStarted ? 'Start over' : 'Back'}
                </button>
                {hasStarted && (
                  <button type="button" className="ci-btn ci-btn-secondary" onClick={handleDownloadResults}>
                    <FileDown size={14} />
                    Download results
                  </button>
                )}
              </>
            )}
            <button
              type="button"
              className="ci-btn ci-btn-primary"
              onClick={() => setConfirmOpen(true)}
              disabled={running || readyCount === 0}
            >
              {running
                ? 'Importing…'
                : `${hasStarted ? 'Resume — import' : 'Import'} ${readyCount} JobSite${readyCount === 1 ? '' : 's'}`}
            </button>
          </div>

          {editRow && (
            <div
              className="ci-modal-overlay"
              role="dialog"
              aria-modal="true"
              aria-labelledby="jsi-edit-row-title"
            >
              <div className="ci-modal">
                <h3 id="jsi-edit-row-title">Correct row {editRow.rowNumber}</h3>
                <p className="ci-modal-hint">
                  Changes are re-validated and duplicate-checked when you save — the row's status updates in place.
                  Leave Model Location Id blank for none.
                </p>
                <div className="ci-modal-grid">
                  {EDIT_FIELDS.map(([label, key], i) => {
                    const isModel = key === 'modelLocationId';
                    return (
                      <label key={key} className="ci-modal-field">
                        <span>{label}</span>
                        <input
                          type="text"
                          autoFocus={i === 0}
                          inputMode={isModel || key === 'zip' ? 'numeric' : undefined}
                          value={isModel ? editModelText : (editRow[key] == null ? '' : String(editRow[key]))}
                          onChange={e => setEditField(key, e.target.value)}
                          aria-invalid={isModel && editModelInvalid ? true : undefined}
                          aria-describedby={isModel && editModelInvalid ? 'jsi-model-error' : undefined}
                        />
                        {isModel && editModelInvalid && (
                          <span id="jsi-model-error" className="jsi-field-error" role="alert">
                            Enter a location id (whole number) or leave blank.
                          </span>
                        )}
                      </label>
                    );
                  })}
                </div>
                <div className="ci-modal-actions">
                  <button
                    type="button"
                    className="ci-btn ci-btn-secondary"
                    onClick={() => setEditRow(null)}
                    disabled={savingEdit}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="ci-btn ci-btn-primary"
                    onClick={handleSaveEdit}
                    disabled={savingEdit || editModelInvalid}
                  >
                    {savingEdit ? 'Saving…' : 'Save & re-validate'}
                  </button>
                </div>
              </div>
            </div>
          )}

          <ConfirmationModal
            isOpen={confirmOpen}
            title={`Create ${readyCount} JobSite${readyCount === 1 ? '' : 's'}?`}
            message={
              `${readyCount} JobSite${readyCount === 1 ? '' : 's'} will be created in batches of ${BATCH_SIZE}. ` +
              `Duplicates and invalid rows are skipped. There is no undo — created JobSites can be edited ` +
              `or promoted afterwards on the JobSites page.`
            }
            confirmText="Create JobSites"
            onConfirm={handleExecute}
            onCancel={() => setConfirmOpen(false)}
          >
            {withoutModel > 0 && (
              <p className="jsi-confirm-note">
                {withoutModel} of them {withoutModel === 1 ? 'has' : 'have'} no model location and won't be usable on orders.
              </p>
            )}
          </ConfirmationModal>
        </div>
      )}

      {step === 'result' && preview && (
        <div className="ci-result-section">
          <div className="ci-result-counts">
            <div className="ci-result-item ci-result-created">
              <CheckCircle size={20} />
              <span><strong>{preview.createdCount}</strong> Created</span>
            </div>
            <div className="ci-result-item ci-result-skipped">
              <AlertCircle size={20} />
              <span><strong>{preview.duplicateCount + preview.invalidCount}</strong> Skipped</span>
            </div>
            <div className="ci-result-item ci-result-failed">
              <XCircle size={20} />
              <span><strong>{preview.failedCount}</strong> Failed</span>
            </div>
          </div>

          {preview.createdCount > 0 && (
            <p className="ci-result-note">
              Download the results — they list the new <strong>Location Id</strong> for every JobSite, which the
              Clinic Matrix "Location Id" column uses. New JobSites have no map position yet: run
              <strong> Location Geocoding</strong> on the Geocoding tab so they show up in clinic searches.
            </p>
          )}

          {totals.errors.length > 0 && (
            <div className="ci-errors-list">
              <h3>Errors</h3>
              <ul>
                {totals.errors.map((e) => <li key={e}>{e}</li>)}
              </ul>
            </div>
          )}

          <div className="jsi-result-actions">
            <button type="button" className="ci-btn ci-btn-primary" onClick={handleDownloadResults}>
              <FileDown size={14} />
              Download results (.xlsx)
            </button>
            {preview.failedCount > 0 && (
              <button
                type="button"
                className="ci-btn ci-btn-secondary"
                onClick={() => { setStep('preview'); setStatusFilter('Failed'); setPage(1); }}
              >
                Review failed rows
              </button>
            )}
            <button type="button" className="ci-btn ci-btn-secondary" onClick={handleReset}>
              Import Another File
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
